import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, CalendarDays, Clock } from "lucide-react";
import { riskLevel, RISK_STYLES, computeRecoveryDay, timeAgo } from "@/lib/appUtils";
import { useBranding } from "@/lib/branding";
import { cn } from "@/lib/utils";

export function PatientCard({ patient }) {
  const { hospCode } = useBranding();
  const rl = riskLevel(patient.risk_score);
  const style = RISK_STYLES[rl];
  const day = patient.recovery_day ?? computeRecoveryDay(patient.surgery_date);
  const dur = patient.care_plan_duration || 20;

  return (
    <Link
      to={`/${hospCode}/doctor/patient/${patient.id}`}
      data-testid={`patient-card-${patient.id}`}
      className="group block surface-card p-5 shadow-sm hover:shadow-md hover:border-foreground/20 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border border-border bg-secondary text-foreground">
            {patient.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-foreground truncate">{patient.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {patient.age} · {patient.gender} · {patient.surgery_type}
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
          <CalendarDays className="w-3 h-3" /> Day {day}/{dur}
        </span>
        <span className={cn("inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border", style.badge)}>
          {style.label} · {patient.risk_score}
        </span>
        {patient.finalized && (
          <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
            Finalized
          </span>
        )}
      </div>

      <div>
        <div className="flex justify-between text-[10px] text-muted-foreground mb-1.5 font-medium uppercase tracking-wider">
          <span>Risk score</span>
          <span>{patient.risk_score}/100</span>
        </div>
        <div className="h-1 rounded-full bg-secondary overflow-hidden">
          <div className={cn("h-full", style.bar)} style={{ width: `${patient.risk_score}%` }} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{patient.contact_phone || "—"}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(patient.last_checkin)}</span>
      </div>
    </Link>
  );
}
